import { compareDesc } from "date-fns";

import { createAppSelector } from "./base";
import {
  selectCurrentBreakoutGroupId,
  selectCurrentListenedBreakoutGroupId,
  selectIsInBreakout,
} from "./breakouts";
import { selectCurrentClassUserId } from "./class-user";
import { selectIsRecordingAgent } from "./permissions";
import {
  selectClassUserIdsPresentInClass,
  selectMaxVisibleParticipants,
  selectRecentSpeakerClassUserIds,
} from "./pubsub-virtual-class-state";
import {
  selectScreensharingClassUserId,
  selectVisibleClassUsersInVirtualClass,
} from "./virtual-classes";

import { ClassUserType, SectionRoleType } from "components/server-types";

export const selectFilteredClassUsersInVirtualClass = createAppSelector(
  [
    selectVisibleClassUsersInVirtualClass,
    selectIsInBreakout,
    selectIsRecordingAgent,
    selectClassUserIdsPresentInClass,
    selectCurrentBreakoutGroupId,
    selectCurrentClassUserId,
  ],
  (
    classUsersInVirtualClass,
    isInBreakout,
    isRecordingAgent,
    presentClassUserIds,
    currentBreakoutId,
  ) => {
    return classUsersInVirtualClass.filter((classUser) => {
      // We will only render participants that are present in the class.
      if (!presentClassUserIds.includes(classUser.id)) return false;

      // If there is currently no breakout session, show all virtual users in the call (main call).
      if (!isInBreakout) return true;

      // Recording agent should not see/hear anyone during a breakout session.
      if (isRecordingAgent) return false;

      if (currentBreakoutId) {
        // If the user is in a breakout group, show only members of the breakout group.
        return classUser.breakout_group_id === currentBreakoutId;
      }
      // If the user is not in a breakout group, show them the main call.
      return classUser.breakout_group_id === null;
    });
  },
);

export const selectClassUsersInListenedBreakoutGroup = createAppSelector(
  [
    selectVisibleClassUsersInVirtualClass,
    selectClassUserIdsPresentInClass,
    selectCurrentListenedBreakoutGroupId,
  ],
  (classUsersInVirtualClass, presentClassUserIds, listenedBreakoutGroupId) => {
    if (!listenedBreakoutGroupId) {
      return [];
    }
    return classUsersInVirtualClass.filter(
      (classUser) =>
        classUser.breakout_group_id === listenedBreakoutGroupId &&
        presentClassUserIds.includes(classUser.id),
    );
  },
);

export const selectVisibleClassUsersInVirtualClassSorted = createAppSelector(
  [
    selectFilteredClassUsersInVirtualClass,
    (state) => state.section_users,
    selectScreensharingClassUserId,
    selectRecentSpeakerClassUserIds,
    selectMaxVisibleParticipants,
    selectCurrentClassUserId,
  ],
  (
    filteredClassUsersInVirtualClass,
    courseUserMap,
    screenshareClassUserId,
    recentSpeakers,
    maxVisibleParticipants,
    currentClassUserId,
  ) => {
    // Sort by role (instructors first, then students)
    const COURSE_ROLE_ORDERING: SectionRoleType[] = ["instructor", "course_admin", "student"];
    const ordered = filteredClassUsersInVirtualClass.toSorted((a, b) => {
      // Always put the current user last
      if (a.id === currentClassUserId) return 1;
      if (b.id === currentClassUserId) return -1;
      // Get the course user data for role comparison
      const courseUserA = courseUserMap[a.section_user_id];
      const courseUserB = courseUserMap[b.section_user_id];

      const roleA = courseUserA?.role || "student";
      const roleB = courseUserB?.role || "student";

      // First sort by role
      if (roleA !== roleB) {
        const aIndex = COURSE_ROLE_ORDERING.indexOf(roleA);
        const bIndex = COURSE_ROLE_ORDERING.indexOf(roleB);
        if (aIndex >= 0 && bIndex >= 0) {
          return aIndex - bIndex;
        }
        // If one of the roles is not in the ordering, we can assume it comes after
        // the roles that are in the ordering.
        return aIndex >= 0 ? -1 : 1;
      }
      // Then sort by who has their hand raised
      if (a.hand_raised_at && b.hand_raised_at) {
        // If both have their hand raised, the person who raised their hand earlier comes first
        return compareDesc(b.hand_raised_at, a.hand_raised_at);
      }
      // If only one has their hand raised, that person comes first
      if (a.hand_raised_at) return -1;
      if (b.hand_raised_at) return 1;

      // If a user is screensharing they go first
      if (screenshareClassUserId) {
        if (a.id === screenshareClassUserId) return -1;
        if (b.id === screenshareClassUserId) return 1;
      }

      // Within the same role, without any raised hands,
      // sort by av_sort_order if available, otherwise fall back to course user ID.
      const aSortOrder = a.av_sort_order;
      const bSortOrder = b.av_sort_order;

      if (aSortOrder !== null && bSortOrder !== null) {
        return aSortOrder - bSortOrder;
      }
      if (aSortOrder !== null) return -1;
      if (bSortOrder !== null) return 1;

      // Fallback to course user ID for consistent ordering
      return a.section_user_id.localeCompare(b.section_user_id);
    });

    // We can show all participants - keep the calculated order
    if (ordered.length <= maxVisibleParticipants) {
      return ordered;
    }

    let currentUser: ClassUserType | undefined;
    if (currentClassUserId) {
      const idx = ordered.findIndex((u) => u.id === currentClassUserId);
      if (idx >= 0) {
        // Remove current user from ordered to handle them separately later
        [currentUser] = ordered.splice(idx, 1);
      }
    }

    const maxVisibleRemote = maxVisibleParticipants - (currentUser ? 1 : 0);

    // Get the visible participants prioritizing recent speakers. We keep the recentSpeakers order as is
    // since it is already by recency.
    let visible = recentSpeakers
      .map((id) => ordered.find((u) => u.id === id))
      // recent speakers may include users not in ordered
      .filter(Boolean)
      .slice(0, maxVisibleRemote);

    // When possible, fill up the list with users from the original order, skipping those already included
    for (const user of ordered) {
      if (visible.length >= maxVisibleRemote) break;
      if (!visible.some((u) => u.id === user.id)) {
        visible.push(user);
      }
    }
    // Now we have a combination of recent speakers and original order filling up the visible slots.
    // Re-sort the visible set by the original order to ensure consistency and avoid shuffling.
    visible = ordered.filter((u) => visible.some((v) => v.id === u.id)).slice(0, maxVisibleRemote);

    if (currentUser) {
      // The currentUser will always be last
      visible.push(currentUser);
    }
    // We build the final list including the hidden but present participants.
    // These are all needed by the ParticipantBar component to render all participants.
    const visibleIds = new Set(visible.map((u) => u.id));
    const hidden = ordered.filter((u) => !visibleIds.has(u.id));

    return [...visible, ...hidden];
  },
);

// Returns class user ids who are hidden due to the size of the participant bar,
// not because they are in a different room
export const selectClassUserIdsHiddenInParticipantBar = createAppSelector(
  [selectVisibleClassUsersInVirtualClassSorted, selectMaxVisibleParticipants],
  (sortedClassUsers, visibleCount) =>
    sortedClassUsers.slice(visibleCount).map((classUser) => classUser.id),
);
